import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import DashboardLayout from "../../../components/DashboardLayout";
import ImageUpload from "../../../components/ImageUpload";
import RenderToast from "../../../components/RenderToast";
import API from "../../../utils/api";
import classes from "./AddProduct.module.css";
import LoaderComp from "../../../components/LoaderComp";

export default function AddProduct() {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    sku: "",
    product_name: "",
    product_short_description: "",
    product_description: "",
    product_brand: [],
    product_category: [],
    product_stocks: "",
    product_meta_title: "",
    product_meta_description: "",
    product_condition: "",
    product_availability: "IN STOCK",
    regular_price: "",
    sale_price: "",
    product_status: "draft",
    product_slug: "",
  });

  const [productImage, setProductImage] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [productGallery, setProductGallery] = useState([]);
  const [productGalleryPreviews, setProductGalleryPreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState("");

  const [newBrandName, setNewBrandName] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newConditionName, setNewConditionName] = useState("");
  const [showNewBrandInput, setShowNewBrandInput] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewConditionInput, setShowNewConditionInput] = useState(false);

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const brandsRes = await API.post("/brands.php", { action: "fetch" });
        const categoriesRes = await API.post("/categories.php", {
          action: "fetch",
        });

        if (brandsRes.status === 200) {
          const brandsData = Array.isArray(brandsRes.data)
            ? brandsRes.data
            : brandsRes.data.data || [];
          setBrands(brandsData);
        }

        if (categoriesRes.status === 200) {
          const categoriesData = Array.isArray(categoriesRes.data)
            ? categoriesRes.data
            : categoriesRes.data.data || [];
          setCategories(categoriesData);
        }
      } catch (err) {
        RenderToast({
          type: "error",
          message: "Failed to load brands/categories",
        });
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ------------------ INPUT HANDLERS ------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ------------------ BRAND ------------------
  const handleBrandSelect = (e) => {
    const brandId = e.target.value;
    if (!brandId) return;
    const brand = brands.find((b) => b.id.toString() === brandId);
    if (brand && !selectedBrands.find((b) => b.id === brand.id)) {
      const newBrands = [...selectedBrands, brand];
      setSelectedBrands(newBrands);
      setFormData((prev) => ({
        ...prev,
        product_brand: newBrands.map((b) => b.id),
      }));
      e.target.value = "";
    }
  };

  const removeBrand = (brandId) => {
    const newBrands = selectedBrands.filter((b) => b.id !== brandId);
    setSelectedBrands(newBrands);
    setFormData((prev) => ({
      ...prev,
      product_brand: newBrands.map((b) => b.id),
    }));
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!newBrandName.trim()) {
      RenderToast({ type: "error", message: "Please enter a brand name" });
      return;
    }
    try {
      const response = await API.post("/brands.php", {
        action: "add",
        brand_name: newBrandName.trim(),
      });
      if (response.status === 200) {
        RenderToast({
          type: "success",
          message: "Brand created successfully!",
        });
        setNewBrandName("");
        setShowNewBrandInput(false);
        const brandsRes = await API.post("/brands.php", { action: "fetch" });
        if (brandsRes.status === 200) {
          const brandsData = Array.isArray(brandsRes.data)
            ? brandsRes.data
            : brandsRes.data.data || [];
          setBrands(brandsData);
        }
      }
    } catch (err) {
      RenderToast({ type: "error", message: "Failed to create brand" });
      console.error(err);
    }
  };

  // ------------------ CATEGORY ------------------
  const handleCategorySelect = (e) => {
    const categoryId = e.target.value;
    if (!categoryId) return;
    const category = categories.find((c) => c.id.toString() === categoryId);
    if (category && !selectedCategory.find((c) => c.id === category.id)) {
      const newCategories = [...selectedCategory, category];
      setSelectedCategory(newCategories);
      setFormData((prev) => ({
        ...prev,
        product_category: newCategories.map((c) => c.id),
      }));
      e.target.value = "";
    }
  };

  const removeCategory = (categoryId) => {
    const newCategories = selectedCategory.filter((c) => c.id !== categoryId);
    setSelectedCategory(newCategories);
    setFormData((prev) => ({
      ...prev,
      product_category: newCategories.map((c) => c.id),
    }));
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      RenderToast({ type: "error", message: "Please enter a category name" });
      return;
    }
    try {
      const response = await API.post("/categories.php", {
        action: "add",
        category_name: newCategoryName.trim(),
        parent_id: "",
      });
      if (response.status === 200) {
        RenderToast({
          type: "success",
          message: "Category created successfully!",
        });
        setNewCategoryName("");
        setShowNewCategoryInput(false);
        const categoriesRes = await API.post("/categories.php", {
          action: "fetch",
        });
        if (categoriesRes.status === 200) {
          const categoriesData = Array.isArray(categoriesRes.data)
            ? categoriesRes.data
            : categoriesRes.data.data || [];
          setCategories(categoriesData);
        }
      }
    } catch (err) {
      RenderToast({ type: "error", message: "Failed to create category" });
      console.error(err);
    }
  };

  // ------------------ CONDITION ------------------
  const handleConditionSelect = (e) => {
    const condition = e.target.value;
    if (!condition) return;
    setSelectedCondition(condition);
    setFormData((prev) => ({ ...prev, product_condition: condition }));
    e.target.value = ""; // Reset dropdown
  };

  const removeCondition = () => {
    setSelectedCondition("");
    setFormData((prev) => ({ ...prev, product_condition: "" }));
  };

  const handleCreateCondition = (e) => {
    e.preventDefault();
    if (!newConditionName.trim()) {
      RenderToast({ type: "error", message: "Please enter a condition name" });
      return;
    }
    const cond = newConditionName.trim().toLowerCase();
    setSelectedCondition(cond);
    setFormData((prev) => ({ ...prev, product_condition: cond }));
    setNewConditionName("");
    setShowNewConditionInput(false);
    RenderToast({ type: "success", message: "Condition added!" });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA")
      e.preventDefault();
  };

  // ------------------ IMAGE HANDLERS ------------------
  const handleImageChange = (file) => {
    setProductImage(file[0] || file);
    const reader = new FileReader();
    reader.onloadend = () => setProductImagePreview(reader.result);
    reader.readAsDataURL(file[0] || file);
  };

  const removeProductImage = () => {
    setProductImage(null);
    setProductImagePreview(null);
  };

  const handleGalleryChange = (files) => {
    if (!files) return;
    const limitedFiles = Array.from(files).slice(0, 5); // max 5 images
    setProductGallery(limitedFiles);

    const previews = [];
    let loadedCount = 0;

    limitedFiles.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews[index] = reader.result;
        loadedCount++;
        if (loadedCount === limitedFiles.length)
          setProductGalleryPreviews([...previews]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setProductGallery(productGallery.filter((_, i) => i !== index));
    setProductGalleryPreviews(
      productGalleryPreviews.filter((_, i) => i !== index),
    );
  };

  // ------------------ FORM VALIDATION ------------------
  const validateForm = () => {
    const newErrors = {};
    if (!formData.sku.trim()) newErrors.sku = "SKU is required";
    if (!formData.product_name.trim())
      newErrors.product_name = "Product Name is required";

    // Validate sale price must be lower than regular price
    const regularPrice = parseFloat(formData.regular_price) || 0;
    const salePrice = parseFloat(formData.sale_price) || 0;
    if (salePrice > 0 && regularPrice > 0 && salePrice >= regularPrice) {
      newErrors.sale_price = "Sale price must be lower than regular price";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ------------------ SUBMIT ------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      RenderToast({ type: "error", message: "Please fix the form errors" });
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Add all form fields
      formDataToSend.append("action", "add");
      formDataToSend.append("sku", formData.sku);
      formDataToSend.append("product_name", formData.product_name);
      formDataToSend.append(
        "product_short_description",
        formData.product_short_description || "",
      );
      formDataToSend.append(
        "product_description",
        formData.product_description || "",
      );
      formDataToSend.append("product_stocks", formData.product_stocks || "0");
      formDataToSend.append(
        "product_meta_title",
        formData.product_meta_title || "",
      );
      formDataToSend.append(
        "product_meta_description",
        formData.product_meta_description || "",
      );
      formDataToSend.append(
        "product_condition",
        formData.product_condition || "",
      );
      formDataToSend.append(
        "product_availability",
        formData.product_availability || "",
      );
      formDataToSend.append("regular_price", formData.regular_price || "0");
      formDataToSend.append("sale_price", formData.sale_price || "");
      formDataToSend.append(
        "product_status",
        formData.product_status || "draft",
      );
      // Schema is auto-generated, no need to send manual schema
      formDataToSend.append("product_schema", "");
      formDataToSend.append("product_slug", formData.product_slug || "");

      // Handle brand - send first brand ID if array, or single ID
      if (
        Array.isArray(formData.product_brand) &&
        formData.product_brand.length > 0
      ) {
        formDataToSend.append("product_brand", formData.product_brand[0]);
      } else if (formData.product_brand) {
        formDataToSend.append("product_brand", formData.product_brand);
      }

      // Handle category - send first category ID if array, or single ID
      if (
        Array.isArray(formData.product_category) &&
        formData.product_category.length > 0
      ) {
        formDataToSend.append("product_category", formData.product_category[0]);
      } else if (formData.product_category) {
        formDataToSend.append("product_category", formData.product_category);
      }

      if (productImage) formDataToSend.append("product_image", productImage);
      productGallery.forEach((file) =>
        formDataToSend.append("product_gallery[]", file),
      );

      const response = await fetch(`${API.base}/products.php`, {
        method: "POST",
        headers: { Authorization: token },
        body: formDataToSend,
      });

      // Check if response is OK and is JSON
      if (!response.ok) {
        const text = await response.text();
        let errorMessage = "Failed to add product";
        try {
          const json = JSON.parse(text);
          errorMessage = json.error || errorMessage;
        } catch {
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.success) {
        RenderToast({
          type: "success",
          message: "Product added successfully!",
        });

        // Reset form
        setFormData({
          sku: "",
          product_name: "",
          product_short_description: "",
          product_description: "",
          product_brand: [],
          product_category: [],
          product_stocks: "",
          product_meta_title: "",
          product_meta_description: "",
          product_condition: "",
          product_availability: "IN STOCK",
          regular_price: "",
          sale_price: "",
          product_status: "draft",
          product_slug: "",
        });
        setProductImage(null);
        setProductImagePreview(null);
        setProductGallery([]);
        setProductGalleryPreviews([]);
        setSelectedBrands([]);
        setSelectedCategory([]);
        setSelectedCondition("");
        setErrors({});
      } else {
        RenderToast({
          type: "error",
          message: data.error || "Failed to add product",
        });
      }
    } catch (err) {
      console.error(err);
      RenderToast({
        type: "error",
        message: "Network error. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <DashboardLayout>
        <LoaderComp />
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <div className={classes.container}>
        <h3 className={classes.heading}>Add Product</h3>
        <form
          className={classes.form}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
        >
          <div className={classes.flexRow}>
            <div className={classes.flexCol}>
              {/* SKU */}
              <div className={classes.formGroup}>
                <label>
                  SKU <span className={classes.required}>*</span>
                </label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  className={errors.sku ? classes.inputError : ""}
                  placeholder="Enter SKU"
                />
                {errors.sku && (
                  <small className={classes.errorText}>{errors.sku}</small>
                )}
              </div>

              {/* Product Name */}
              <div className={classes.formGroup}>
                <label>
                  Product Name <span className={classes.required}>*</span>
                </label>
                <input
                  type="text"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  className={errors.product_name ? classes.inputError : ""}
                  placeholder="Enter product name"
                />
                {errors.product_name && (
                  <small className={classes.errorText}>
                    {errors.product_name}
                  </small>
                )}
              </div>

              {/* Product Status */}
              <div className={classes.formGroup}>
                <label>
                  Product Status <span className={classes.required}>*</span>
                </label>
                <select
                  name="product_status"
                  value={formData.product_status}
                  onChange={handleInputChange}
                >
                  <option value="draft">Draft (hidden on storefront)</option>
                  <option value="publish">
                    Publish (visible on storefront)
                  </option>
                </select>
                <p className={classes.inputHint}>
                  Draft keeps the product internal until you are ready to
                  publish it.
                </p>
              </div>

              {/* Short Description */}
              <div className={classes.formGroup}>
                <label>Short Description</label>
                <textarea
                  name="product_short_description"
                  value={formData.product_short_description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Enter short description"
                />
              </div>

              {/* Description */}
              <div className={classes.formGroup}>
                <label>Description</label>
                <textarea
                  name="product_description"
                  value={formData.product_description}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="Enter detailed description"
                />
              </div>

              {/* Brands */}
              <div className={classes.formGroup}>
                <label>Brands</label>
                <select
                  onChange={handleBrandSelect}
                  defaultValue=""
                  style={{ marginBottom: "0.5rem" }}
                >
                  <option value="">Select Brand</option>
                  {brands
                    .filter((b) => !selectedBrands.find((sb) => sb.id === b.id))
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.brandName}
                      </option>
                    ))}
                </select>
                {!showNewBrandInput ? (
                  <button
                    type="button"
                    className={classes.addBtn}
                    onClick={() => setShowNewBrandInput(true)}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    + Add New Brand
                  </button>
                ) : (
                  <div
                    className={classes.newInputGroup}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    <input
                      type="text"
                      placeholder="Enter brand name"
                      value={newBrandName}
                      onChange={(e) => setNewBrandName(e.target.value)}
                    />
                    <button
                      type="button"
                      className={classes.confirmBtn}
                      onClick={handleCreateBrand}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className={classes.cancelBtn}
                      onClick={() => {
                        setShowNewBrandInput(false);
                        setNewBrandName("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {selectedBrands.length > 0 && (
                  <div className={classes.selectedItems}>
                    {selectedBrands.map((b) => (
                      <span key={b.id} className={classes.badge}>
                        <span>{b.brandName}</span>{" "}
                        <FiX onClick={() => removeBrand(b.id)} />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className={classes.formGroup}>
                <label>Categories</label>
                <select
                  onChange={handleCategorySelect}
                  defaultValue=""
                  style={{ marginBottom: "0.5rem" }}
                >
                  <option value="">Select Category</option>
                  {categories
                    .filter(
                      (c) => !selectedCategory.find((sc) => sc.id === c.id),
                    )
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.categoryName}
                      </option>
                    ))}
                </select>
                {!showNewCategoryInput ? (
                  <button
                    type="button"
                    className={classes.addBtn}
                    onClick={() => setShowNewCategoryInput(true)}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    + Add New Category
                  </button>
                ) : (
                  <div
                    className={classes.newInputGroup}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    <input
                      type="text"
                      placeholder="Enter category name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                    <button
                      type="button"
                      className={classes.confirmBtn}
                      onClick={handleCreateCategory}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className={classes.cancelBtn}
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategoryName("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {selectedCategory.length > 0 && (
                  <div className={classes.selectedItems}>
                    {selectedCategory.map((c) => (
                      <span key={c.id} className={classes.badge}>
                        <span>{c.categoryName}</span>{" "}
                        <FiX onClick={() => removeCategory(c.id)} />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Condition */}
              <div className={classes.formGroup}>
                <label>Condition</label>
                <select
                  value=""
                  onChange={handleConditionSelect}
                  style={{ marginBottom: "0.5rem" }}
                >
                  <option value="">Select Condition</option>
                  {selectedCondition !== "new" && (
                    <option value="new">New</option>
                  )}
                  {selectedCondition !== "refurbished" && (
                    <option value="refurbished">Refurbished</option>
                  )}
                  {selectedCondition !== "used" && (
                    <option value="used">Used</option>
                  )}
                  {selectedCondition !== "like-new" && (
                    <option value="like-new">Like New</option>
                  )}
                </select>
                {!showNewConditionInput ? (
                  <button
                    type="button"
                    className={classes.addBtn}
                    onClick={() => setShowNewConditionInput(true)}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    + Add New Condition
                  </button>
                ) : (
                  <div
                    className={classes.newInputGroup}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    <input
                      type="text"
                      placeholder="Enter condition"
                      value={newConditionName}
                      onChange={(e) => setNewConditionName(e.target.value)}
                    />
                    <button
                      type="button"
                      className={classes.confirmBtn}
                      onClick={handleCreateCondition}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className={classes.cancelBtn}
                      onClick={() => {
                        setShowNewConditionInput(false);
                        setNewConditionName("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {selectedCondition && (
                  <div className={classes.selectedItems}>
                    <span className={classes.badge}>
                      <span>{selectedCondition}</span>{" "}
                      <FiX onClick={removeCondition} />
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className={classes.flexCol}>
              {/* Stocks */}
              <div className={classes.formGroup}>
                <label>Stock</label>
                <input
                  type="number"
                  name="product_stocks"
                  value={formData.product_stocks}
                  onChange={handleInputChange}
                  placeholder="Enter stock quantity"
                />
              </div>

              {/* Availability */}
              <div className={classes.formGroup}>
                <label>Availability</label>
                <input
                  type="text"
                  name="product_availability"
                  value={formData.product_availability}
                  onChange={handleInputChange}
                  placeholder="e.g., IN STOCK, OUT OF STOCK"
                />
              </div>

              {/* Regular Price */}
              <div className={classes.formGroup}>
                <label>Regular Price</label>
                <input
                  type="number"
                  name="regular_price"
                  value={formData.regular_price}
                  onChange={handleInputChange}
                  placeholder="Enter regular price"
                />
              </div>

              {/* Sale Price */}
              <div className={classes.formGroup}>
                <label>Sale Price</label>
                <input
                  type="number"
                  name="sale_price"
                  value={formData.sale_price}
                  onChange={handleInputChange}
                  className={errors.sale_price ? classes.inputError : ""}
                  placeholder="Enter sale price (optional)"
                />
                {errors.sale_price && (
                  <small className={classes.errorText}>
                    {errors.sale_price}
                  </small>
                )}
              </div>

              {/* Meta Title */}
              <div className={classes.formGroup}>
                <label>Meta Title</label>
                <input
                  type="text"
                  name="product_meta_title"
                  value={formData.product_meta_title}
                  onChange={handleInputChange}
                  placeholder="Enter meta title"
                />
              </div>

              {/* Meta Description */}
              <div className={classes.formGroup}>
                <label>Meta Description</label>
                <textarea
                  name="product_meta_description"
                  value={formData.product_meta_description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Enter meta description"
                />
              </div>

              {/* Product Slug */}
              <div className={classes.formGroup}>
                <label>Product Slug</label>
                <input
                  type="text"
                  name="product_slug"
                  value={formData.product_slug}
                  onChange={handleInputChange}
                  placeholder="e.g., product-name-sku"
                />
                <p className={classes.inputHint}>
                  Optional: Custom product slug for SEO-friendly URLs
                </p>
              </div>

              {/* Product Image */}
              <ImageUpload
                label="Product Image"
                onImageChange={handleImageChange}
                onRemove={removeProductImage}
                preview={productImagePreview}
              />

              {/* Product Gallery */}
              <ImageUpload
                label="Product Gallery (Max 5 images)"
                onImageChange={handleGalleryChange}
                onRemove={removeGalleryImage}
                preview={productGalleryPreviews}
                multiple={true}
                maxFiles={5}
              />
            </div>
          </div>

          <div className={classes.submitWrapper}>
            <button
              type="submit"
              disabled={submitting}
              className={classes.submitBtn}
            >
              {submitting ? "Submitting..." : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
